/**
 * Route construction utility for V0 application
 *
 * Provides type-safe, programmatic route construction for all V0 routes.
 *
 * Usage examples:
 * - Routes.dataset.new('my-dataset-id')
 * - Routes.dataset.forId('dataset-123')
 * - Routes.dataset.column.new('dataset-123')
 * - Routes.dataset.column.llm('dataset-123')
 * - Routes.dataset.column.edit('dataset-123', 'column-456')
 * - Routes.run.forKey('run-789').forFrameKey('frame-101')
 */

type RouteBuilder = {
  toString(): string;
};

// Base route builder that accumulates path segments
class BaseRouteBuilder implements RouteBuilder {
  constructor(readonly segments: string[] = []) {}

  addSegment(segment: string): BaseRouteBuilder {
    return new BaseRouteBuilder([...this.segments, segment]);
  }

  toString(): string {
    return '/' + this.segments.join('/');
  }
}

// Dataset routes
class DatasetRouteBuilder extends BaseRouteBuilder {
  constructor(segments: string[] = ['dataset']) {
    super(segments);
  }

  /**
   * Create a new dataset route
   * @param id - The dataset ID
   * @returns Route to new dataset page
   */
  new(id: string): RouteBuilder {
    return new BaseRouteBuilder([...this.segments, 'new', id]);
  }

  /**
   * Navigate to a specific dataset
   * @param id - The dataset ID
   * @returns Route to dataset detail page
   */
  forId(id: string): DatasetDetailRouteBuilder {
    return new DatasetDetailRouteBuilder([
      ...this.segments,
      encodeURIComponent(id),
    ]);
  }
}

// Dataset detail routes (when you have a specific dataset)
class DatasetDetailRouteBuilder extends BaseRouteBuilder {
  constructor(segments: string[] = ['dataset', ':id']) {
    super(segments);
  }

  /**
   * Navigate to column management for this dataset
   */
  get column(): DatasetColumnRouteBuilder {
    return new DatasetColumnRouteBuilder([...this.segments, 'column']);
  }
}

// Dataset column routes
class DatasetColumnRouteBuilder extends BaseRouteBuilder {
  constructor(segments: string[] = ['dataset', ':id', 'column']) {
    super(segments);
  }

  /**
   * Create a new column for the dataset
   * @param datasetId - The dataset ID
   * @returns Route to new column page
   */
  new(datasetId: string): DatasetColumnNewRouteBuilder {
    return new DatasetColumnNewRouteBuilder([
      'dataset',
      encodeURIComponent(datasetId),
      'column',
      'new',
    ]);
  }

  /**
   * Edit an existing column
   * @param datasetId - The dataset ID
   * @param columnId - The column ID
   * @returns Route to edit column page
   */
  edit(datasetId: string, columnId: string): DatasetColumnEditRouteBuilder {
    return new DatasetColumnEditRouteBuilder([
      'dataset',
      encodeURIComponent(datasetId),
      'column',
      encodeURIComponent(columnId),
    ]);
  }
}

// Dataset column new routes
class DatasetColumnNewRouteBuilder extends BaseRouteBuilder {
  constructor(segments: string[] = ['dataset', ':id', 'column', 'new']) {
    super(segments);
  }

  /**
   * Create a new LLM column
   * @param datasetId - The dataset ID
   * @returns Route to new LLM column page
   */
  llm(datasetId: string): RouteBuilder {
    return new BaseRouteBuilder([
      'dataset',
      encodeURIComponent(datasetId),
      'column',
      'new',
      'llm',
    ]);
  }

  /**
   * Create a new expression column
   * @param datasetId - The dataset ID
   * @returns Route to new expression column page
   */
  expression(datasetId: string): RouteBuilder {
    return new BaseRouteBuilder([
      'dataset',
      encodeURIComponent(datasetId),
      'column',
      'new',
      'expression',
    ]);
  }

  /**
   * Create a new taxonomy column
   * @param datasetId - The dataset ID
   * @returns Route to new taxonomy column page
   */
  taxonomy(datasetId: string): RouteBuilder {
    return new BaseRouteBuilder([
      'dataset',
      encodeURIComponent(datasetId),
      'column',
      'new',
      'taxonomy',
    ]);
  }

  /**
   * Create a new tool call column
   * @param datasetId - The dataset ID
   * @returns Route to new tool call column page
   */
  toolCall(datasetId: string): RouteBuilder {
    return new BaseRouteBuilder([
      'dataset',
      encodeURIComponent(datasetId),
      'column',
      'new',
      'tool-call',
    ]);
  }
}

// Dataset column edit routes
class DatasetColumnEditRouteBuilder extends BaseRouteBuilder {
  constructor(segments: string[] = ['dataset', ':id', 'column', ':columnId']) {
    super(segments);
  }

  /**
   * Navigate to ATC (stack frame) view for this column
   * @param datasetId - The dataset ID
   * @param columnId - The column ID
   * @returns Route to ATC view
   */
  atc(datasetId: string, columnId: string): RouteBuilder {
    return new BaseRouteBuilder([
      'dataset',
      encodeURIComponent(datasetId),
      'column',
      encodeURIComponent(columnId),
      'atc',
    ]);
  }
}

// REPL routes
class ReplRouteBuilder extends BaseRouteBuilder {
  constructor(segments: string[] = ['repl']) {
    super(segments);
  }

  /**
   * Navigate to a specific REPL
   * @param id - The REPL ID
   * @returns Route to REPL page
   */
  forId(id: string): RouteBuilder {
    return this.addSegment(encodeURIComponent(id));
  }
}

// Document routes
class DocumentRouteBuilder extends BaseRouteBuilder {
  constructor(segments: string[] = ['document']) {
    super(segments);
  }

  /**
   * Navigate to a specific document
   * @param id - The document ID
   * @returns Route to document page
   */
  forId(id: string): RouteBuilder {
    return this.addSegment(encodeURIComponent(id));
  }

  /**
   * Navigate to a new document
   * @returns Route to new document page
   */
  newDoc(): RouteBuilder {
    return this.addSegment('new').addSegment(
      Math.random().toString(36).substring(2, 10)
    );
  }
}

// Tool routes
class ToolRouteBuilder extends BaseRouteBuilder {
  constructor(segments: string[] = ['tool']) {
    super(segments);
  }

  /**
   * Navigate to a specific tool
   * @param id - The tool ID
   * @returns Route to tool page
   */
  forId(id: string): RouteBuilder {
    return this.addSegment(encodeURIComponent(id));
  }

  /**
   * Navigate to a new tool
   * @returns Route to new tool page
   */
  newTool(): RouteBuilder {
    return this.addSegment('new').addSegment(
      Math.random().toString(36).substring(2, 10)
    );
  }
}

// Program REPL routes
class ProgramReplRouteBuilder extends BaseRouteBuilder {
  constructor(segments: string[] = ['program-repl']) {
    super(segments);
  }

  /**
   * Navigate to a specific program REPL
   * @param id - The program REPL ID
   * @returns Route to program REPL page
   */
  forId(id: string): RouteBuilder {
    return this.addSegment(encodeURIComponent(id));
  }
}

// Definition routes
class DefinitionRouteBuilder extends BaseRouteBuilder {
  constructor(segments: string[] = ['definition']) {
    super(segments);
  }

  /**
   * Navigate to a specific definition
   * @param id - The definition ID
   * @returns Route to definition page
   */
  forId(id: string): RouteBuilder {
    return this.addSegment(encodeURIComponent(id));
  }
}

// Run routes
class RunRouteBuilder extends BaseRouteBuilder {
  constructor(segments: string[] = ['run']) {
    super(segments);
  }

  /**
   * Navigate to a specific run
   * @param runKey - The run key
   * @returns Route builder for run details
   */
  forKey(runKey: string): RunDetailRouteBuilder {
    return new RunDetailRouteBuilder([
      ...this.segments,
      encodeURIComponent(runKey),
    ]);
  }
}

// Run detail routes
class RunDetailRouteBuilder extends BaseRouteBuilder {
  constructor(segments: string[] = ['run', ':runKey']) {
    super(segments);
  }

  /**
   * Navigate to a specific frame within the run
   * @param runKey - The run key
   * @param frameKey - The frame key
   * @returns Route to run frame page
   */
  forFrameKey(runKey: string, frameKey: string): RouteBuilder {
    return new BaseRouteBuilder([
      'run',
      encodeURIComponent(runKey),
      encodeURIComponent(frameKey),
    ]);
  }
}

// Runs (history) routes
class RunsRouteBuilder extends BaseRouteBuilder {
  constructor(segments: string[] = ['runs']) {
    super(segments);
  }

  /**
   * Navigate to a specific run history
   * @param runKey - The run key
   * @returns Route to run history page
   */
  forKey(runKey: string): RouteBuilder {
    return this.addSegment(encodeURIComponent(runKey));
  }
}

// Agent routes
class AgentRouteBuilder extends BaseRouteBuilder {
  constructor(segments: string[] = ['agent']) {
    super(segments);
  }

  /**
   * Navigate to a specific agent
   * @param id - The agent ID
   * @returns Route to agent page
   */
  forId(id: string): RouteBuilder {
    return this.addSegment(encodeURIComponent(id));
  }

  /**
   * Navigate to a new agent
   * @returns Route to new agent page
   */
  newAgent(): RouteBuilder {
    return this.addSegment('new').addSegment(
      Math.random().toString(36).substring(2, 10)
    );
  }
}

// Main Routes object
export const Routes = {
  /**
   * Dataset-related routes
   */
  get dataset(): DatasetRouteBuilder {
    return new DatasetRouteBuilder();
  },

  /**
   * REPL routes
   */
  get repl(): ReplRouteBuilder {
    return new ReplRouteBuilder();
  },

  /**
   * Document routes
   */
  get document(): DocumentRouteBuilder {
    return new DocumentRouteBuilder();
  },

  /**
   * Tool routes
   */
  get tool(): ToolRouteBuilder {
    return new ToolRouteBuilder();
  },

  /**
   * Program REPL routes
   */
  get programRepl(): ProgramReplRouteBuilder {
    return new ProgramReplRouteBuilder();
  },

  /**
   * Definition routes
   */
  get definition(): DefinitionRouteBuilder {
    return new DefinitionRouteBuilder();
  },

  /**
   * Run routes (single run details)
   */
  get run(): RunRouteBuilder {
    return new RunRouteBuilder();
  },

  /**
   * Runs routes (run history)
   */
  get runs(): RunsRouteBuilder {
    return new RunsRouteBuilder();
  },

  /**
   * Agent routes
   */
  get agent(): AgentRouteBuilder {
    return new AgentRouteBuilder();
  },

  /**
   * Static routes
   */
  static: {
    identities: '/identities',
    operations: '/operations',
  },
} as const;
